import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Minus, Plus, User, Users, Flame } from 'lucide-react';
import {
    INDIVIDUAL_TIERS, TEAM_TIERS,
    TEAM_SEAT_PRICING, computeTeamPrice, computeTeamAnnualTotal, computeTeamCredits,
    FOUNDING_CONFIG, hasFoundingPrice, getFoundingPrice,
} from '../../constants/tiers';
import billingService from '../../services/billing';
import FoundingBanner from './FoundingBanner';

// Paid individual plans (Creator, Pro, Studio) — Free is shown separately below
const PAID_TIERS = INDIVIDUAL_TIERS.filter(t => t.monthlyPrice > 0);
const FREE_TIER  = INDIVIDUAL_TIERS.find(t => t.id === 'free');

// ── Seat stepper ────────────────────────────────────────────────────────────────
const SeatStepper = ({ seats, setSeats }) => {
    const { minSeats, maxSeats } = TEAM_SEAT_PRICING;
    return (
        <div className="flex items-center gap-3">
            <button
                onClick={() => setSeats(s => Math.max(minSeats, s - 1))}
                disabled={seats <= minSeats}
                className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
                <Minus size={13} />
            </button>
            <div className="min-w-[3.5rem] text-center">
                <span className="text-2xl font-heading font-black text-white">{seats}</span>
                <span className="text-xs text-white/40 ml-1">seats</span>
            </div>
            <button
                onClick={() => setSeats(s => Math.min(maxSeats, s + 1))}
                disabled={seats >= maxSeats}
                className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
                <Plus size={13} />
            </button>
        </div>
    );
};

// ── Team tier card (special — has seat selector) ────────────────────────────────
const TeamCard = ({ tier, billing, seatCount, setSeatCount }) => {
    const displayPrice   = computeTeamPrice(seatCount, billing);
    const annualTotal    = computeTeamAnnualTotal(seatCount);
    const credits        = computeTeamCredits(seatCount);

    return (
        <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex flex-col rounded-2xl p-6 border border-[var(--primary-color)] bg-[rgba(24,181,240,0.04)] shadow-[0_0_40px_rgba(24,181,240,0.1)]"
        >
            {tier.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 rounded-full bg-[var(--primary-color)] text-black text-[10px] font-bold uppercase tracking-wider shadow-[0_0_12px_rgba(24,181,240,0.4)]">
                        {tier.badge}
                    </span>
                </div>
            )}

            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)] mb-3">
                {tier.name}
            </p>

            {/* Dynamic price */}
            <div className="mb-1">
                <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-heading font-black text-white">${displayPrice}</span>
                    <span className="text-sm text-[var(--text-secondary)]">/ mo</span>
                </div>
                {billing === 'annual' ? (
                    <p className="mt-1 text-[10px] text-white/30">${annualTotal.toLocaleString()} billed annually</p>
                ) : (
                    <p className="mt-1 text-[10px] text-white/30">or ${computeTeamPrice(seatCount, 'annual')}/mo billed annually</p>
                )}
            </div>

            <p className="text-xs text-[var(--text-secondary)] mt-2 mb-5 leading-relaxed">
                {tier.description}
            </p>

            {/* Seat selector */}
            <div className="mb-4 rounded-xl bg-white/4 border border-white/8 p-3">
                <p className="text-[10px] text-white/40 font-bold uppercase tracking-widest mb-2.5">Seats</p>
                <div className="flex items-center justify-between">
                    <SeatStepper seats={seatCount} setSeats={setSeatCount} />
                    <span className="text-[10px] text-white/30">3 – 15 seats</span>
                </div>
            </div>

            {/* Credits pill */}
            <div className="mb-4 rounded-xl bg-white/4 border border-white/8 divide-y divide-white/6 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest">Credits / mo</span>
                    <span className="text-sm font-black text-white">{credits.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest">Shared pool</span>
                    <span className="text-sm font-black text-white">{seatCount} members</span>
                </div>
            </div>

            {/* Features */}
            <ul className="flex-1 space-y-2.5 mb-8">
                {tier.features.map(feat => (
                    <li key={feat} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                        <Check size={13} className="text-[var(--primary-color)] mt-0.5 shrink-0" />
                        {feat}
                    </li>
                ))}
            </ul>

            <Link
                to="/signup"
                className="block text-center py-3 rounded-xl text-sm font-bold bg-[var(--primary-color)] text-black hover:brightness-110 shadow-[0_0_16px_rgba(24,181,240,0.35)] transition-all w-full"
            >
                {tier.cta}
            </Link>
        </motion.div>
    );
};

// ── Main component ──────────────────────────────────────────────────────────────
const LandingPricing = () => {
    const [billing,   setBilling]   = useState('monthly');    // 'monthly' | 'annual'
    const [planType,  setPlanType]  = useState('individual'); // 'individual' | 'team'
    // Founding 50 status. `available` flips off automatically once Stripe's
    // FOUNDING50 coupon hits its 50-redemption cap. While available, the
    // Individual tier cards render the discounted price with the regular
    // price shown struck-through, and the FoundingBanner above announces
    // it explicitly.
    const [foundingAvailable, setFoundingAvailable] = useState(FOUNDING_CONFIG.enabled);
    useEffect(() => {
        if (!FOUNDING_CONFIG.enabled) return;
        billingService.getFoundingSpots()
            .then((res) => {
                const remaining = res?.data?.remaining ?? res?.remaining;
                if (typeof remaining === 'number') setFoundingAvailable(remaining > 0);
            })
            .catch(() => {/* keep optimistic default */});
    }, []);
    const [seatCount, setSeatCount] = useState(TEAM_SEAT_PRICING.minSeats);

    return (
        <section id="pricing" className="relative py-32 px-6">
            {/* Ambient bg */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#0D52BC] opacity-[0.05] blur-[120px] rounded-full" />
            </div>

            <div className="max-w-6xl mx-auto">

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-12"
                >
                    <span className="inline-block text-[var(--primary-color)] text-xs font-bold uppercase tracking-widest mb-4">
                        Pricing
                    </span>
                    <h2 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-white">
                        Free to start. Cheap to go wild.
                    </h2>
                    <p className="mt-4 text-lg text-[var(--text-secondary)] max-w-xl mx-auto">
                        No surprises. No contracts. Cancel whenever. We're not clingy.
                    </p>
                </motion.div>

                {/* Founding 50 banner removed — was confusing scarcity copy
                    ("50/50 spots left") when the counter hadn't actually been
                    incrementing. Reinstate when the live-counter wiring is
                    verified end-to-end if we want the urgency-CTA back. */}

                {/* Controls */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="flex flex-col items-center gap-5 mb-14"
                >
                    {/* Individual / Team tabs */}
                    <div className="flex bg-white/5 border border-white/10 rounded-xl p-1 gap-1">
                        <button
                            onClick={() => setPlanType('individual')}
                            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-all ${
                                planType === 'individual'
                                    ? 'bg-white/10 text-white shadow-sm'
                                    : 'text-white/40 hover:text-white/70'
                            }`}
                        >
                            <User size={13} /> Individual
                        </button>
                        <button
                            onClick={() => setPlanType('team')}
                            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-all ${
                                planType === 'team'
                                    ? 'bg-white/10 text-white shadow-sm'
                                    : 'text-white/40 hover:text-white/70'
                            }`}
                        >
                            <Users size={13} /> Team &amp; Studio
                        </button>
                    </div>

                    {/* Monthly / Annual toggle */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setBilling('monthly')}
                            className={`text-sm font-bold transition-colors ${billing === 'monthly' ? 'text-white' : 'text-white/40 hover:text-white/60'}`}
                        >
                            Monthly
                        </button>
                        {/* Billing toggle — gradient track when active with a
                             cyan glow halo, inset shadow when idle, knob has
                             its own gradient + soft shadow so it reads as a
                             physical pill against the track. */}
                        <button
                            type="button"
                            role="switch"
                            aria-checked={billing === 'annual'}
                            aria-label="Toggle annual billing"
                            onClick={() => setBilling(v => v === 'monthly' ? 'annual' : 'monthly')}
                            className={`group relative w-14 h-7 rounded-full transition-all duration-300 ease-out ${
                                billing === 'annual'
                                    ? 'shadow-[0_0_22px_-2px_rgba(24,181,240,0.55),inset_0_0_0_1px_rgba(255,255,255,0.15)]'
                                    : 'shadow-[inset_0_1px_2px_rgba(0,0,0,0.4),inset_0_0_0_1px_rgba(255,255,255,0.08)] hover:shadow-[inset_0_1px_2px_rgba(0,0,0,0.4),inset_0_0_0_1px_rgba(255,255,255,0.18)]'
                            }`}
                            style={{
                                background: billing === 'annual'
                                    ? 'linear-gradient(135deg, #18b5f0 0%, #0d52bc 100%)'
                                    : 'rgba(255,255,255,0.06)',
                            }}
                        >
                            <span
                                className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-active:scale-95 ${
                                    billing === 'annual' ? 'translate-x-7' : 'translate-x-0'
                                }`}
                                style={{
                                    background: 'linear-gradient(180deg, #ffffff 0%, #d6dde6 100%)',
                                    boxShadow: billing === 'annual'
                                        ? '0 2px 6px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9)'
                                        : '0 2px 6px rgba(0,0,0,0.4), 0 0 0 1px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.85)',
                                }}
                            />
                        </button>
                        <button
                            onClick={() => setBilling('annual')}
                            className={`text-sm font-bold transition-colors ${billing === 'annual' ? 'text-white' : 'text-white/40 hover:text-white/60'}`}
                        >
                            Annual
                        </button>
                        <AnimatePresence>
                            {billing === 'annual' && (
                                <motion.span
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    className="px-2.5 py-1 rounded-full bg-[var(--primary-color)]/15 text-[var(--primary-color)] text-[10px] font-bold uppercase tracking-wider border border-[var(--primary-color)]/20"
                                >
                                    Save up to 20%
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>

                {/* ── Plan cards ─────────────────────────────────────────────────── */}
                <AnimatePresence mode="wait">

                    {/* Individual: 3 paid cards */}
                    {planType === 'individual' && (
                        <motion.div
                            key="individual"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                {PAID_TIERS.map((tier, i) => {
                                    const regularPrice = billing === 'annual' ? tier.annualPrice : tier.monthlyPrice;
                                    // Founding price is 50% off and only shown when (a) the
                                    // FOUNDING_CONFIG enabled flag is on AND (b) Stripe still
                                    // has redemption slots. Once sold out we revert to regular
                                    // pricing automatically — no manual config flip needed.
                                    const foundingPrice = foundingAvailable && hasFoundingPrice(tier.id)
                                        ? getFoundingPrice(tier.id, billing)
                                        : null;
                                    const showFounding = foundingPrice != null;
                                    const price = showFounding ? foundingPrice : regularPrice;
                                    return (
                                        <motion.div
                                            key={tier.id}
                                            initial={{ opacity: 0, y: 32 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                                            className={`relative flex flex-col rounded-2xl p-6 border transition-all ${
                                                tier.highlight
                                                    ? 'border-[var(--primary-color)] bg-[rgba(24,181,240,0.04)] shadow-[0_0_40px_rgba(24,181,240,0.1)]'
                                                    : 'border-white/8 glass-panel hover:border-white/15'
                                            }`}
                                        >
                                            {tier.badge && (
                                                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                                                    <span className="px-3 py-1 rounded-full bg-[var(--primary-color)] text-black text-[10px] font-bold uppercase tracking-wider shadow-[0_0_12px_rgba(24,181,240,0.4)]">
                                                        {tier.badge}
                                                    </span>
                                                </div>
                                            )}

                                            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)] mb-3">
                                                {tier.name}
                                            </p>

                                            <div className="mb-5">
                                                <div className="flex items-baseline gap-2">
                                                    <span className={`text-4xl font-heading font-black ${showFounding ? 'text-amber-200' : 'text-white'}`}>${price}</span>
                                                    <span className="text-sm text-[var(--text-secondary)]">/ mo</span>
                                                    {showFounding && (
                                                        <span className="text-base text-white/30 line-through font-heading font-bold">
                                                            ${regularPrice}
                                                        </span>
                                                    )}
                                                </div>
                                                {showFounding && (
                                                    <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-300">
                                                        <Flame size={10} fill="currentColor" />
                                                        Founding 50 — 50% off forever
                                                    </div>
                                                )}
                                                {billing === 'annual' && tier.annualTotal && !showFounding && (
                                                    <p className="mt-1 text-[10px] text-white/30">${tier.annualTotal.toLocaleString()} billed annually</p>
                                                )}
                                                {billing === 'monthly' && tier.annualPrice && !showFounding && (
                                                    <p className="mt-1 text-[10px] text-white/30">or ${tier.annualPrice}/mo billed annually</p>
                                                )}
                                                <p className="text-xs text-[var(--text-secondary)] mt-2.5 leading-relaxed">
                                                    {tier.description}
                                                </p>
                                            </div>

                                            {/* Credits pill */}
                                            <div className="mb-4 rounded-xl bg-white/4 border border-white/8 overflow-hidden">
                                                <div className="flex items-center justify-between px-3 py-2">
                                                    <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest">Credits / mo</span>
                                                    <span className="text-sm font-black text-white">{tier.credits.toLocaleString()}</span>
                                                </div>
                                            </div>

                                            <ul className="flex-1 space-y-2.5 mb-8">
                                                {tier.features.map(feat => (
                                                    <li key={feat} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                                                        <Check size={13} className="text-[var(--primary-color)] mt-0.5 shrink-0" />
                                                        {feat}
                                                    </li>
                                                ))}
                                            </ul>

                                            <Link
                                                // Query string carries the user's plan choice + Founding intent
                                                // through signup so the post-register flow can fire Stripe Checkout
                                                // with foundingMember:true automatically — no manual coupon entry.
                                                // billing=annual is also passed when the user has the toggle on.
                                                to={`/signup?plan=${tier.id}&billing=${billing}${showFounding ? '&founding=1' : ''}`}
                                                className={`block text-center py-3 rounded-xl text-sm font-bold transition-all w-full ${
                                                    tier.highlight
                                                        ? 'bg-[var(--primary-color)] text-black hover:brightness-110 shadow-[0_0_16px_rgba(24,181,240,0.35)]'
                                                        : 'border border-white/15 text-white hover:border-white/30 hover:bg-white/5'
                                                }`}
                                            >
                                                {tier.cta}
                                            </Link>
                                        </motion.div>
                                    );
                                })}
                            </div>

                            {/* ── Free tier — subtle banner below ── */}
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                                className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-white/6 bg-white/[0.025] px-6 py-4"
                            >
                                <div className="flex items-center gap-4">
                                    <span className="text-xl font-heading font-black text-white/30">$0</span>
                                    <div>
                                        <p className="text-sm font-bold text-white/50">Free forever</p>
                                        <p className="text-xs text-white/30 mt-0.5">
                                            100 credits · 1 project · 5 GB storage · no credit card required
                                        </p>
                                    </div>
                                </div>
                                <Link
                                    to="/signup"
                                    className="shrink-0 px-4 py-2 rounded-lg border border-white/10 text-white/40 text-xs font-bold hover:text-white/70 hover:border-white/20 transition-all"
                                >
                                    Get Started Free
                                </Link>
                            </motion.div>
                        </motion.div>
                    )}

                    {/* Team: Team card + Enterprise card */}
                    {planType === 'team' && (
                        <motion.div
                            key="team"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.3 }}
                            className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto"
                        >
                            <TeamCard
                                tier={TEAM_TIERS[0]}
                                billing={billing}
                                seatCount={seatCount}
                                setSeatCount={setSeatCount}
                            />

                            {/* Enterprise card */}
                            {(() => {
                                const ent = TEAM_TIERS[1];
                                return (
                                    <motion.div
                                        initial={{ opacity: 0, y: 32 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: 0.07, ease: [0.16, 1, 0.3, 1] }}
                                        className="relative flex flex-col rounded-2xl p-6 border border-white/8 glass-panel hover:border-white/15 transition-all"
                                    >
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)] mb-3">
                                            {ent.name}
                                        </p>
                                        <div className="mb-5">
                                            <span className="text-4xl font-heading font-black text-white">Custom</span>
                                            <p className="text-xs text-[var(--text-secondary)] mt-2.5 leading-relaxed">{ent.description}</p>
                                        </div>
                                        <ul className="flex-1 space-y-2.5 mb-8">
                                            {ent.features.map(feat => (
                                                <li key={feat} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                                                    <Check size={13} className="text-[var(--primary-color)] mt-0.5 shrink-0" />
                                                    {feat}
                                                </li>
                                            ))}
                                        </ul>
                                        <Link
                                            to="/signup"
                                            className="block text-center py-3 rounded-xl text-sm font-bold border border-white/15 text-white hover:border-white/30 hover:bg-white/5 transition-all w-full"
                                        >
                                            {ent.cta}
                                        </Link>
                                    </motion.div>
                                );
                            })()}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Annual savings nudge */}
                <AnimatePresence>
                    {billing === 'monthly' && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="text-center mt-10 text-xs text-white/25"
                        >
                            Annual billing saves Creator $48/yr · Pro $120/yr · Studio $360/yr
                        </motion.p>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};

export default LandingPricing;
